import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Calendar,
  MapPin,
  Clock,
  Users,
  IndianRupee,
  Sparkles,
  Coffee,
  MessageSquareQuote,
  Compass,
  Sunrise,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Ticket,
} from "lucide-react";

// Apps Script endpoint that writes to the Worth The Years registrations Sheet
// AND sends a confirmation email. See /worth-the-years-apps-script.gs at the repo
// root for the script you need to paste into the Sheet's Apps Script editor.
// Replace this placeholder /exec URL with the one you get after deploying.
const GAS_WEB_APP_URL =
  "https://script.google.com/macros/s/AKfycbyh7FmkQsqQq6ARVnIlahGOjmHhXUSSAz3sgFd3YkO0cFFpEPS-g25eSbOHVP0pHoQ-Dw/exec";

const STUDENT_PRICE = 350;
const PARENT_PRICE = 0;

const SPEAKERS = [
  {
    n: "01",
    field: "Architecture",
    quote: "Life is beautiful!",
    name: "Bijith Bhaskar",
    role: "Architect • Design Leader",
    org: "Founder, ENSEMBLEE",
    bio: "Architect, planner and sustainability advocate with 25+ years of experience and 450+ signature projects across India and overseas.",
    bullets: [
      "Founder & Principal Architect, ENSEMBLEE (National Award Winning Practice)",
      "Pioneer in green buildings & sustainable communities through EARTHSOUL",
      "Expertise in Architecture, Interiors, Master Planning & Educational Campuses",
      "Collaborates with global firms in UK and Singapore; strong in BIM",
    ],
    initials: "BB",
    image: "/team/bijith-bhaskar.png",
  },
  {
    n: "02",
    field: "Fashion",
    quote: "Made to last.",
    name: "Kavita Mallick",
    role: "Fashion Retail Consultant",
    org: "Business Leader • Visionary • Enabler",
    bio: "Fashion business leader with deep expertise in retail, brand building and strategic consulting for future-ready businesses.",
    bullets: [
      "Freelance Fashion Retail Consultant | Independent Director",
      "Former Head of Brand, MiniKlub",
      "Former Head of Buying, Lifestyle International",
      "Ex-Category Manager (South Zone), Pantaloons",
      "B.Tech in Fashion Technology, NIFT Delhi",
    ],
    initials: "KM",
    image: "/team/kavita-mallick.jpeg",
  },
  {
    n: "03",
    field: "Academia / Discourse",
    quote: "Paid to wonder.",
    name: "Dr. Sheelan Misra",
    role: "Academic Leader • Education Advisor",
    org: "Founder, UniThink Education",
    bio: "Leader in higher education, international & industry relations, and strategy. Building bridges between education, industry and global opportunities.",
    bullets: [
      "CEO, UniThink Education International",
      "Spearheading Marketing Strategy, International Relations & Industry Relations, Soma Star Innovation",
      "Secretary – Administration, Confederation of Women Entrepreneurs – India",
      "Former VP, Higher Education Partnerships, Yangpoo Harvard Business Publishing",
    ],
    initials: "SM",
    image: "/team/sheelan-misra.jpeg",
  },
];

const HIGHLIGHTS = [
  { icon: Sunrise, label: "Sunday Tea to Connect" },
  { icon: MessageSquareQuote, label: "Globally Recognized" },
  { icon: Users, label: "Career Counselling & Psychometric Assessment" },
  { icon: Compass, label: "Bonus: 30-Minute Vaastu Master Class", featured: true },
  { icon: Coffee, label: "Snacks Included" },
];

const SERIES_PILLARS = [
  {
    icon: Sunrise,
    title: "Morning sessions with Tea",
    body: "Inspiring talks in the morning followed by a curated tea to connect and reflect.",
  },
  {
    icon: Compass,
    title: "3 careers. 1 direction.",
    body: "Explore three diverse careers that share a common shift or future.",
  },
  {
    icon: Sparkles,
    title: "Personalised for you",
    body: "One-on-one career counselling & psychometric assessment at every session.",
  },
];

const SESSION_STEPS = [
  { n: "01", title: "A Shift", sub: "The future direction" },
  { n: "02", title: "3 Careers", sub: "Different paths within it" },
  { n: "03", title: "Real Stories", sub: "Practitioners. Real-world." },
  { n: "04", title: "Your Next Step", sub: "Clarity through counselling" },
];

const SERIES_FACTS = [
  { icon: Clock, label: "When", v: "Sundays · 10:00 AM – 1:00 PM" },
  { icon: MapPin, label: "Where", v: "Curated spaces across Bengaluru (different every week)" },
  { icon: Coffee, label: "Includes", v: "Talks, Tea, counselling & more" },
  { icon: Users, label: "For", v: "Students 15–18 & their parents" },
];

// The three Ds — the editorial centerpiece of Session 01
const THREE_DS = [
  {
    n: "01",
    category: "Domains",
    title: "Architecture",
    quote: "Life is beautiful!",
    tone: "from-[#8B2E1F]/15 via-[#FAF3E0] to-[#C9A227]/10",
    accent: "#8B2E1F",
    motif: "architecture",
    speaker: "Bijith Bhaskar",
    image: "/three-ds/architecture.jpg",
  },
  {
    n: "02",
    category: "Dynamics",
    title: "Fashion",
    quote: "Made to last.",
    tone: "from-[#C9A227]/15 via-[#FAF3E0] to-[#8B2E1F]/10",
    accent: "#8B2E1F",
    motif: "fashion",
    speaker: "Kavita Mallick",
    image: "/three-ds/fashion.jpg",
  },
  {
    n: "03",
    category: "Discourse",
    title: "Academia",
    quote: "Paid to wonder.",
    tone: "from-[#1A1410]/15 via-[#FAF3E0] to-[#8B2E1F]/10",
    accent: "#8B2E1F",
    motif: "academia",
    speaker: "Dr. Sheelan Misra",
    image: "/three-ds/academia.jpg",
  },
];

// ───────────────────────────────────────────────────────────────────────────
// Animated grain / noise overlay for premium texture
const GrainOverlay = () => (
  <div
    className="pointer-events-none fixed inset-0 z-[1] opacity-[0.07] mix-blend-multiply"
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
    }}
  />
);

// Animated headline reveal — word-by-word
const RevealWords = ({ text, className = "", delay = 0 }) => {
  const words = text.split(" ");
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom mr-[0.25em]">
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: delay + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

// Calm ambient hero backdrop — soft washes, gentle motion, no visual noise
const AmbientBackdrop = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <motion.div
      className="absolute -top-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-[#8B2E1F]/8 blur-3xl"
      animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
      transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute -bottom-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-[#C9A227]/10 blur-3xl"
      animate={{ x: [0, -30, 0], y: [0, -40, 0] }}
      transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);

// Editorial line-art collage behind the headline — architecture × fashion × education
const HeroMotifs = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {/* Faint blueprint grid */}
    <div
      className="absolute inset-0 opacity-[0.05]"
      style={{
        backgroundImage:
          "linear-gradient(#1A1410 1px, transparent 1px), linear-gradient(90deg, #1A1410 1px, transparent 1px)",
        backgroundSize: "72px 72px",
      }}
    />

    {/* Connecting sweep curves between the three motifs */}
    <svg
      className="absolute inset-0 w-full h-full opacity-[0.18]"
      viewBox="0 0 1600 900"
      preserveAspectRatio="none"
      fill="none"
      stroke="#8B2E1F"
      strokeWidth="1"
    >
      <motion.path
        d="M -50 320 C 360 240, 720 540, 1100 380 S 1700 480, 1700 480"
        strokeDasharray="3 6"
        animate={{ pathLength: [0.9, 1, 0.9] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.path
        d="M -50 640 C 320 720, 720 500, 1120 660 S 1700 580, 1700 580"
        stroke="#C9A227"
        strokeWidth="0.8"
        animate={{ pathLength: [0.9, 1, 0.9] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
    </svg>

    {/* ARCHITECTURE — top-left building elevation */}
    <motion.div
      className="absolute top-[32%] md:top-[14%] left-[3%] md:left-[7%] w-20 sm:w-28 md:w-56 text-[#8B2E1F]/35 md:text-[#8B2E1F]/45"
      animate={{ y: [0, -10, 0], rotate: [-1, 1, -1] }}
      transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg viewBox="0 0 200 260" fill="none" stroke="currentColor" strokeWidth="1.4">
        {/* Roof */}
        <path d="M40 70 L100 30 L160 70" />
        {/* Body */}
        <rect x="40" y="70" width="120" height="170" />
        {/* Floor lines */}
        <line x1="40" y1="110" x2="160" y2="110" />
        <line x1="40" y1="150" x2="160" y2="150" />
        <line x1="40" y1="190" x2="160" y2="190" />
        {/* Vertical mullions */}
        <line x1="80" y1="70" x2="80" y2="240" />
        <line x1="120" y1="70" x2="120" y2="240" />
        {/* Dimension bracket */}
        <line x1="22" y1="70" x2="22" y2="240" strokeDasharray="3 4" strokeWidth="0.9" />
        <line x1="17" y1="70" x2="27" y2="70" strokeWidth="0.9" />
        <line x1="17" y1="240" x2="27" y2="240" strokeWidth="0.9" />
      </svg>
    </motion.div>

    {/* Decorative set-square between architecture and headline */}
    <motion.div
      className="absolute top-[6%] left-[26%] w-16 md:w-24 text-[#1A1410]/30 hidden md:block"
      animate={{ rotate: [-6, 4, -6] }}
      transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M20 30 L180 30 L20 180 Z" />
        <line x1="60" y1="30" x2="60" y2="60" />
        <line x1="100" y1="30" x2="100" y2="80" />
        <line x1="140" y1="30" x2="140" y2="60" />
      </svg>
    </motion.div>

    {/* EDUCATION — top-right mortarboard + open book */}
    <motion.div
      className="absolute top-[30%] md:top-[10%] right-[3%] md:right-[7%] w-20 sm:w-28 md:w-52 text-[#1A1410]/30 md:text-[#1A1410]/40"
      animate={{ y: [0, 12, 0], rotate: [1, -1.5, 1] }}
      transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg viewBox="0 0 200 240" fill="none" stroke="currentColor" strokeWidth="1.4">
        {/* Mortarboard */}
        <path d="M20 60 L100 30 L180 60 L100 90 Z" />
        <path d="M55 78 L55 120 C 55 138, 145 138, 145 120 L 145 78" />
        <line x1="170" y1="60" x2="170" y2="115" />
        <circle cx="170" cy="120" r="3" fill="currentColor" />
        {/* Open book */}
        <path d="M30 170 L100 155 L170 170 L170 215 L100 200 L30 215 Z" />
        <line x1="100" y1="155" x2="100" y2="200" />
        <line x1="50" y1="178" x2="85" y2="172" strokeWidth="0.8" />
        <line x1="50" y1="188" x2="85" y2="182" strokeWidth="0.8" />
        <line x1="115" y1="172" x2="150" y2="178" strokeWidth="0.8" />
        <line x1="115" y1="182" x2="150" y2="188" strokeWidth="0.8" />
      </svg>
    </motion.div>

    {/* Decorative pencil on right */}
    <motion.div
      className="absolute top-[44%] right-[2%] w-12 md:w-16 text-[#C9A227]/45 hidden md:block"
      animate={{ y: [0, -6, 0], rotate: [42, 38, 42] }}
      transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg viewBox="0 0 60 240" fill="none" stroke="currentColor" strokeWidth="1.2">
        <rect x="20" y="30" width="20" height="170" />
        <path d="M20 200 L30 230 L40 200" />
        <line x1="30" y1="200" x2="30" y2="225" />
        <line x1="20" y1="50" x2="40" y2="50" />
        <path d="M20 30 L30 10 L40 30" />
      </svg>
    </motion.div>

    {/* FASHION — bottom dress form */}
    <motion.div
      className="absolute bottom-[6%] md:bottom-[8%] left-1/2 -translate-x-1/2 w-24 sm:w-32 md:w-44 text-[#8B2E1F]/40"
      animate={{ y: [0, -8, 0], rotate: [-1.5, 1.5, -1.5] }}
      transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg viewBox="0 0 200 280" fill="none" stroke="currentColor" strokeWidth="1.4">
        {/* Hanger */}
        <path d="M100 18 C 100 8, 112 8, 112 20" />
        <line x1="100" y1="20" x2="100" y2="40" />
        {/* Dress form */}
        <path d="M100 40 C 80 50, 75 65, 80 78 C 60 95, 50 145, 60 200 L 40 270 L 160 270 L 140 200 C 150 145, 140 95, 120 78 C 125 65, 120 50, 100 40 Z" />
        {/* Center seam — stitched */}
        <line x1="100" y1="78" x2="100" y2="270" strokeDasharray="4 4" strokeWidth="1" />
        {/* Waist line */}
        <path d="M58 160 C 100 168, 100 168, 142 160" strokeDasharray="2 3" strokeWidth="0.9" />
      </svg>
    </motion.div>

    {/* Decorative tape measure bottom-left */}
    <motion.div
      className="absolute bottom-[18%] left-[5%] md:left-[10%] w-28 md:w-40 text-[#C9A227]/40 hidden sm:block"
      animate={{ rotate: [-3, 3, -3] }}
      transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg viewBox="0 0 220 80" fill="none" stroke="currentColor" strokeWidth="1.1">
        <rect x="10" y="30" width="200" height="16" />
        {Array.from({ length: 21 }).map((_, i) => (
          <line
            key={i}
            x1={10 + i * 10}
            y1="30"
            x2={10 + i * 10}
            y2={i % 5 === 0 ? 18 : 24}
          />
        ))}
      </svg>
    </motion.div>

    {/* Decorative thread spool */}
    <motion.div
      className="absolute bottom-[8%] right-[8%] md:right-[14%] w-12 md:w-16 text-[#1A1410]/35 hidden md:block"
      animate={{ rotate: [0, 360] }}
      transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
    >
      <svg viewBox="0 0 100 120" fill="none" stroke="currentColor" strokeWidth="1.2">
        <ellipse cx="50" cy="20" rx="35" ry="8" />
        <line x1="15" y1="20" x2="15" y2="100" />
        <line x1="85" y1="20" x2="85" y2="100" />
        <ellipse cx="50" cy="100" rx="35" ry="8" />
        <line x1="20" y1="40" x2="80" y2="50" strokeWidth="0.7" />
        <line x1="20" y1="60" x2="80" y2="70" strokeWidth="0.7" />
        <line x1="20" y1="80" x2="80" y2="90" strokeWidth="0.7" />
      </svg>
    </motion.div>

    {/* Tiny discipline labels orbiting the headline (desktop only) */}
    <div className="absolute inset-0 hidden md:flex items-center justify-center">
      <div className="relative w-[32rem] h-[32rem] opacity-60">
        <span className="absolute top-0 left-1/2 -translate-x-1/2 text-[9px] tracking-[0.5em] uppercase text-[#8B2E1F]/70 font-medium">
          Architecture
        </span>
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[9px] tracking-[0.5em] uppercase text-[#8B2E1F]/70 font-medium">
          Fashion
        </span>
        <span className="absolute top-1/2 -translate-y-1/2 right-0 -rotate-90 origin-right text-[9px] tracking-[0.5em] uppercase text-[#8B2E1F]/70 font-medium">
          Academia
        </span>
      </div>
    </div>
  </div>
);


// Marquee strip
const Marquee = ({ items }) => (
  <div className="relative overflow-hidden border-y border-[#8B2E1F]/20 bg-[#1A1410] text-[#FAF3E0]">
    <motion.div
      className="flex whitespace-nowrap py-4 text-sm md:text-base tracking-[0.3em] uppercase"
      animate={{ x: ["0%", "-50%"] }}
      transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
    >
      {[...items, ...items, ...items, ...items].map((t, i) => (
        <span key={i} className="mx-8 flex items-center gap-8">
          {t}
          <span className="text-[#C9A227]">✦</span>
        </span>
      ))}
    </motion.div>
  </div>
);

// Countdown to the event
const useCountdown = (target) => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
};

const EVENT_DATE = new Date("2026-05-31T10:00:00+05:30").getTime();

// ───────────────────────────────────────────────────────────────────────────
export default function WorthTheYears() {
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    school: "",
    parents: 0,
    parent1Name: "",
    parent2Name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ ok: null, msg: "" });
  const [showStickyBar, setShowStickyBar] = useState(false);

  const totalAmount = STUDENT_PRICE + form.parents * PARENT_PRICE;
  const totalAttendees = 1 + form.parents;

  const heroCTARef = useRef(null);
  const registerRef = useRef(null);

  const cd = useCountdown(EVENT_DATE);

  // Show sticky bar once hero CTA scrolls out, hide when registration form is in view
  useEffect(() => {
    const handleScroll = () => {
      const heroCTA = heroCTARef.current;
      const registerSection = registerRef.current;
      if (!heroCTA) return;

      const heroCTABottom = heroCTA.getBoundingClientRect().bottom;
      const shouldShow = heroCTABottom < 0;

      // Hide when registration form is visible
      if (registerSection) {
        const registerTop = registerSection.getBoundingClientRect().top;
        const registerBottom = registerSection.getBoundingClientRect().bottom;
        if (registerTop < window.innerHeight && registerBottom > 0) {
          setShowStickyBar(false);
          return;
        }
      }

      setShowStickyBar(shouldShow);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const writeToSheet = async (payload) => {
    const qs = new URLSearchParams(payload).toString();
    await fetch(GAS_WEB_APP_URL, {
      method: "POST",
      mode: "no-cors",
      body: qs,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setStatus({ ok: null, msg: "" });

    if (!form.fullName || !form.phone || !form.email) {
      setStatus({ ok: false, msg: "Please fill all required fields." });
      return;
    }
    const cleanPhone = form.phone.replace(/[\s-]/g, "");
    if (!/^[6-9][0-9]{9}$/.test(cleanPhone)) {
      setStatus({ ok: false, msg: "Enter a valid 10-digit Indian mobile number." });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setStatus({ ok: false, msg: "Please enter a valid email address." });
      return;
    }
    if (form.parents >= 1 && !form.parent1Name.trim()) {
      setStatus({ ok: false, msg: "Please add Parent 1's name." });
      return;
    }
    if (form.parents >= 2 && !form.parent2Name.trim()) {
      setStatus({ ok: false, msg: "Please add Parent 2's name." });
      return;
    }

    if (!window.Razorpay) {
      setStatus({
        ok: false,
        msg: "Payment library not loaded. Refresh the page and try again.",
      });
      return;
    }

    setSubmitting(true);

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: totalAmount * 100,
      currency: "INR",
      name: "Worth The Years · UniThink Education",
      description: "Session 01: The 3Ds of Design — 31 May 2026",
      image: "https://unithink.in/favicon.ico",
      prefill: {
        name: form.fullName,
        email: form.email,
        contact: cleanPhone,
      },
      notes: {
        event: "WorthTheYears_S1_3DsOfDesign",
        attendees: String(totalAttendees),
        parents: String(form.parents),
      },
      theme: { color: "#8B2E1F" },
      modal: {
        ondismiss: () => {
          setSubmitting(false);
          setStatus({ ok: false, msg: "Payment cancelled. Your seat isn't reserved yet." });
        },
      },
      handler: async (response) => {
        if (window.fbq) {
          window.fbq("track", "Lead", {
            value: totalAmount,
            currency: "INR",
            content_name: "Worth The Years - Session 01",
          });
        }

        try {
          await writeToSheet({
            fullName: form.fullName,
            phone: cleanPhone,
            email: form.email,
            school: form.school,
            parents: String(form.parents),
            parent1Name: form.parent1Name,
            parent2Name: form.parent2Name,
            amount: String(totalAmount),
            razorpayPaymentId: response.razorpay_payment_id,
            source: "WorthTheYears_S1_3DsOfDesign",
            eventDate: "2026-05-31",
          });
        } catch {
          // payment is captured by Razorpay regardless; sheet write is best-effort
        }
        setSubmitting(false);
        setStatus({
          ok: true,
          msg: `Payment received (₹${totalAmount}). Confirmation sent to ${form.email}.`,
        });
        setForm({
          fullName: "",
          phone: "",
          email: "",
          school: "",
          parents: 0,
          parent1Name: "",
          parent2Name: "",
        });
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => {
      setSubmitting(false);
      setStatus({
        ok: false,
        msg:
          (resp && resp.error && resp.error.description) ||
          "Payment failed. Please try again.",
      });
    });
    rzp.open();
  };

  const scrollToForm = () => {
    document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative bg-[#FAF3E0] text-[#1A1410] font-sans selection:bg-[#8B2E1F] selection:text-[#FAF3E0] overflow-x-hidden">
      <GrainOverlay />

      {/* ───── STICKY FLOATING CTA BAR ───── */}
      <AnimatePresence>
        {showStickyBar && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50"
          >
            <div className="bg-[#1A1410]/95 backdrop-blur-xl border-t border-[#C9A227]/30 shadow-[0_-8px_40px_-12px_rgba(26,20,16,0.5)]">
              <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
                <div className="hidden sm:flex items-center gap-3 text-[#FAF3E0]">
                  <div className="h-9 w-9 rounded-full bg-[#8B2E1F]/30 flex items-center justify-center">
                    <Ticket size={16} className="text-[#C9A227]" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Session 01 · The 3Ds of Design</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#FAF3E0]/55">
                      Sun, 31 May · ₹350 total · Limited seats
                    </div>
                  </div>
                </div>
                <button
                  onClick={scrollToForm}
                  className="group relative inline-flex items-center gap-2 bg-[#8B2E1F] text-[#FAF3E0] px-6 py-3 rounded-full text-sm font-medium overflow-hidden transition-all hover:bg-[#C9A227] hover:text-[#1A1410] sm:px-8"
                >
                  <span className="relative z-10">Reserve my seat</span>
                  <ArrowRight size={16} className="relative z-10 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ───── HERO ───── */}
      <section
        className="relative min-h-screen pt-28 pb-20 px-4 md:px-8 overflow-hidden"
      >
        {/* Layered illustration collage — architecture × fashion × education */}
        <AmbientBackdrop />
        <HeroMotifs />

        <motion.div
          className="relative z-10 max-w-7xl mx-auto"
        >
          {/* top bar */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col sm:flex-row items-center sm:justify-between mb-10 md:mb-16 gap-4 sm:gap-0 text-center sm:text-left"
          >
            <div className="flex flex-col items-center sm:items-start gap-1.5 sm:gap-2">
              <span className="text-xs sm:text-sm tracking-[0.35em] uppercase text-[#1A1410]/70 font-medium">
                A Weekly Talk Series
              </span>
              <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#8B2E1F] font-semibold">
                By ENSEMBLEE <span className="text-[#8B2E1F]/65 px-1 font-medium">in association with</span> UniThink
              </span>
            </div>
            <span className="hidden sm:inline-block text-xs sm:text-sm tracking-[0.3em] uppercase text-[#8B2E1F] font-medium">
              Mornings that change everything
            </span>
          </motion.div>

          {/* Brand title */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-6"
          >
            <div className="inline-flex items-center gap-2 sm:gap-3 text-[11px] sm:text-sm md:text-base tracking-[0.25em] sm:tracking-[0.35em] md:tracking-[0.4em] uppercase text-[#1A1410]/70 whitespace-nowrap">
              <span className="h-px w-5 sm:w-8 md:w-10 bg-[#1A1410]/30" />
              <span className="inline-flex items-center">
                W
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8B2E1F"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                  className="inline-block mx-[0.08em] align-[-0.06em]"
                  style={{ width: "0.85em", height: "0.85em" }}
                >
                  <circle cx="12" cy="12" r="9" />
                  <line x1="12" y1="3.5" x2="12" y2="11" />
                </svg>
                RTH&nbsp;THE&nbsp;YEARS
              </span>
              <span className="h-px w-5 sm:w-8 md:w-10 bg-[#1A1410]/30" />
            </div>
            <div className="mt-3 text-[10px] sm:text-xs md:text-sm tracking-[0.4em] sm:tracking-[0.5em] uppercase text-[#8B2E1F] font-semibold">
              Session 01
            </div>
          </motion.div>

          {/* Headline — elegant editorial, no shimmer */}
          <h1 className="text-center font-serif font-light leading-[0.95] text-[13vw] md:text-[8.5rem] tracking-tight">
            <RevealWords text="THE" delay={0.4} />
            <motion.span
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="inline-block mx-3 md:mx-6 align-middle italic font-serif text-[#8B2E1F]"
            >
              3Ds
            </motion.span>
            <RevealWords text="OF DESIGN" delay={0.8} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="text-center mt-8 text-base md:text-2xl tracking-[0.35em] uppercase text-[#1A1410]/80 font-light"
          >
            3 Careers <span className="text-[#8B2E1F]">·</span> 1 Direction
          </motion.p>

          {/* Hero meta + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.8 }}
            className="mt-12 md:mt-16 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {[
              { icon: Calendar, label: "Sun, 31 May 2026", sub: "10:00 AM – 1:00 PM" },
              { icon: MapPin, label: "ENSEMBLEE", sub: "Indiranagar, Bengaluru" },
              { icon: Ticket, label: "₹350 total", sub: "Includes student & up to 2 parents" },
            ].map((m, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -4 }}
                className="group relative bg-white/40 backdrop-blur-md border border-[#1A1410]/10 rounded-2xl p-5 flex items-center gap-4 transition-all hover:border-[#8B2E1F]/40 hover:bg-white/60"
              >
                <div className="h-11 w-11 rounded-xl bg-[#8B2E1F]/10 flex items-center justify-center text-[#8B2E1F] group-hover:bg-[#8B2E1F] group-hover:text-[#FAF3E0] transition-all">
                  <m.icon size={20} />
                </div>
                <div>
                  <div className="font-semibold text-[#1A1410] md:text-lg">{m.label}</div>
                  <div className="text-sm text-[#1A1410]/60">{m.sub}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Countdown */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 0.8 }}
            className="mt-10 flex justify-center"
          >
            <div className="flex items-center gap-3 md:gap-6 bg-[#1A1410] text-[#FAF3E0] px-6 py-4 rounded-full">
              {[
                { v: cd.d, l: "Days" },
                { v: cd.h, l: "Hrs" },
                { v: cd.m, l: "Min" },
                { v: cd.s, l: "Sec" },
              ].map((u, i) => (
                <React.Fragment key={i}>
                  <div className="text-center">
                    <div className="text-xl md:text-2xl font-mono font-semibold tabular-nums">
                      {String(u.v).padStart(2, "0")}
                    </div>
                    <div className="text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#FAF3E0]/60">
                      {u.l}
                    </div>
                  </div>
                  {i < 3 && <span className="text-[#C9A227]/60">:</span>}
                </React.Fragment>
              ))}
            </div>
          </motion.div>

          {/* Primary CTA */}
          <motion.div
            ref={heroCTARef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.2, duration: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4"
          >
            <button
              onClick={scrollToForm}
              className="group relative inline-flex items-center gap-3 bg-[#8B2E1F] text-[#FAF3E0] px-8 py-4 rounded-full text-base font-medium overflow-hidden transition-all hover:bg-[#1A1410]"
            >
              <span className="relative z-10">Reserve my seat</span>
              <ArrowRight size={18} className="relative z-10 transition-transform group-hover:translate-x-1" />
              <span className="absolute inset-0 bg-[#C9A227] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            </button>
            <a
              href="#speakers"
              className="inline-flex items-center gap-2 text-base font-medium text-[#1A1410]/70 hover:text-[#8B2E1F] transition-colors"
            >
              Meet the speakers <ChevronDown size={18} className="animate-bounce" />
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* ───── HIGHLIGHTS ───── */}
      <section className="relative py-24 md:py-32 px-4 md:px-8 bg-[#1A1410] text-[#FAF3E0] overflow-hidden z-10">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #C9A227 0, transparent 40%), radial-gradient(circle at 80% 70%, #8B2E1F 0, transparent 40%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="text-xs tracking-[0.4em] uppercase text-[#C9A227] font-medium mb-4">
              What's included
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">
              More than a talk. <br className="hidden md:block" />
              <em className="text-[#C9A227]">A morning</em> worth remembering.
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                whileHover={{ y: -6 }}
                className={`relative p-6 rounded-2xl border transition-all ${h.featured
                  ? "bg-[#8B2E1F] border-[#C9A227] shadow-[0_0_40px_-10px_#C9A227]"
                  : "bg-[#FAF3E0]/5 border-[#FAF3E0]/10 hover:border-[#C9A227]/40"
                  }`}
              >
                {h.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A227] text-[#1A1410] text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1 rounded-full">
                    Bonus
                  </span>
                )}
                <h.icon size={28} className={h.featured ? "text-[#C9A227]" : "text-[#C9A227]"} />
                <div className="mt-4 text-base md:text-lg font-medium leading-snug">
                  {h.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ───── THE THREE Ds — editorial centerpiece ───── */}
      <section id="three-ds" className="relative py-20 md:py-28 px-4 md:px-8 z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <div className="inline-flex items-center gap-4 text-[10px] md:text-xs tracking-[0.5em] uppercase text-[#1A1410]/55 mb-5">
              <span className="h-px w-8 md:w-12 bg-[#1A1410]/30" />
              The Three Ds
              <span className="h-px w-8 md:w-12 bg-[#1A1410]/30" />
            </div>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] text-[#1A1410] max-w-2xl mx-auto">
              3 Careers, <em className="text-[#8B2E1F]">1 Direction.</em>
            </h2>
            <p className="mt-6 max-w-xl mx-auto text-lg md:text-xl text-[#1A1410]/70 leading-relaxed">
              Sunday morning conversations with practitioners about thoughtful work,
              cultural intelligence, and lives well-lived.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {THREE_DS.map((d, i) => (
              <ThreeDPanel key={d.n} d={d} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ───── MARQUEE ───── */}
      <Marquee
        items={[
          "Good conversations",
          "Better decisions",
          "Brighter years",
          "Worth The Years",
          "Session 01 — The 3Ds of Design",
        ]}
      />

      {/* ───── ABOUT THE SERIES ───── 
      <section className="relative py-24 md:py-32 px-4 md:px-8 z-10">
        <div className="max-w-7xl mx-auto">
          {/* Heading 
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <div className="text-xs tracking-[0.4em] uppercase text-[#8B2E1F] font-medium mb-5">
              The Series
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-8">
              Mornings that <em className="text-[#8B2E1F]">change</em> everything.
            </h2>
            <p className="text-base md:text-lg tracking-[0.25em] uppercase text-[#1A1410]/70 leading-relaxed">
              Conversations with practitioners about careers that matter,
              <br className="hidden md:block" /> and lives well-lived.
            </p>
          </motion.div>

          {/* 3 Pillars 
      <div className="grid md:grid-cols-3 gap-5 md:gap-8 mb-24">
        {SERIES_PILLARS.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.6 }}
            whileHover={{ y: -6 }}
            className="relative bg-white border border-[#1A1410]/10 rounded-3xl p-8 md:p-10 hover:border-[#8B2E1F]/40 transition-all overflow-hidden"
          >
            <span className="absolute top-6 right-7 font-mono text-[10px] tracking-[0.3em] text-[#1A1410]/30">
              0{i + 1}
            </span>
            <div className="h-14 w-14 rounded-2xl bg-[#8B2E1F]/10 flex items-center justify-center text-[#8B2E1F] mb-7">
              <p.icon size={26} />
            </div>
            <h3 className="text-xs sm:text-sm tracking-[0.3em] uppercase text-[#1A1410] font-bold mb-3">
              {p.title}
            </h3>
            <p className="text-base md:text-lg text-[#1A1410]/70 leading-relaxed">{p.body}</p>
            <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#8B2E1F] to-[#C9A227] group-hover:w-full transition-all duration-500" />
          </motion.div>
        ))}
      </div>

      {/* Series facts row 
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 py-10 md:py-12 border-y border-[#1A1410]/15 mb-24">
        {SERIES_FACTS.map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="flex items-start gap-4"
          >
            <div className="shrink-0 h-10 w-10 rounded-full border border-[#8B2E1F]/30 flex items-center justify-center text-[#8B2E1F]">
              <f.icon size={16} />
            </div>
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#1A1410]/50 mb-1">
                {f.label}
              </div>
              <div className="text-base md:text-lg font-medium text-[#1A1410] leading-snug">
                {f.v}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Each session covers — 4-step journey 
      <div className="mb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
        >
          <div>
            <div className="text-xs tracking-[0.4em] uppercase text-[#8B2E1F] font-medium mb-3">
              Each session covers
            </div>
            <h3 className="font-serif text-3xl md:text-5xl leading-tight">
              A four-step <em className="text-[#8B2E1F]">journey,</em> every Sunday.
            </h3>
          </div>
          <p className="text-sm text-[#1A1410]/60 md:max-w-xs">
            One arc. Four moves. From a shift in the world to a step in your life.
          </p>
        </motion.div>

        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-0">
          {SESSION_STEPS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="relative md:px-6 md:border-l md:first:border-l-0 border-[#1A1410]/15"
            >
              <div className="font-mono text-sm tracking-[0.3em] text-[#8B2E1F] mb-4">
                {s.n}
              </div>
              <div className="font-serif text-2xl md:text-3xl leading-tight mb-2">
                {s.title}
              </div>
              <div className="text-base text-[#1A1410]/65 leading-relaxed">{s.sub}</div>
              {/* Connecting arrow on desktop 
              {i < SESSION_STEPS.length - 1 && (
                <ArrowRight
                  size={18}
                  className="hidden md:block absolute -right-2 top-1 text-[#8B2E1F]/50"
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Every week. New possibilities. callout 
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="relative bg-[#1A1410] text-[#FAF3E0] rounded-3xl px-8 md:px-16 py-14 md:py-20 text-center overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #C9A227 0, transparent 45%), radial-gradient(circle at 80% 70%, #8B2E1F 0, transparent 45%)",
          }}
        />
        <div className="relative">
          <h3 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-5">
            Every week. <em className="text-[#C9A227]">New possibilities.</em>
          </h3>
          <p className="text-lg md:text-xl text-[#FAF3E0]/75 max-w-2xl mx-auto">
            Step into a morning that can shape the next decade of your life.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase text-[#C9A227]/80 font-medium">
            <span className="h-px w-10 bg-[#C9A227]/50" />
            Starting May 2026
            <span className="h-px w-10 bg-[#C9A227]/50" />
          </div>
        </div>
      </motion.div>

      {/* Bridge into Session 01 
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="text-center mt-20 text-lg md:text-xl text-[#1A1410]/70 max-w-3xl mx-auto leading-relaxed"
      >
        And this Sunday, Session 01 brings together an architect, a fashion business
        leader, and an academic to ask the same question across three vastly different
        careers:
        <span className="block mt-2 text-[#1A1410] font-medium">
          what does it mean to design a life that lasts?
        </span>
      </motion.p>
    </div>
      </section >*/
      }

      {/* ───── SPEAKERS ───── */}
      <section id="speakers" className="relative py-20 md:py-28 px-4 md:px-8 z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="text-xs tracking-[0.4em] uppercase text-[#8B2E1F] font-medium mb-4">
              Meet our speakers
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">
              Three crafts. <em className="text-[#8B2E1F]">One conversation.</em>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {SPEAKERS.map((s, i) => (
              <SpeakerCard key={s.name} s={s} idx={i} />
            ))}
          </div>
        </div>
      </section>


      {/* ───── REGISTRATION ───── */}
      <section id="register" ref={registerRef} className="relative py-24 md:py-32 px-4 md:px-8 z-10">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-start">
          {/* Event details column */}
          <div>
            <div className="text-xs tracking-[0.4em] uppercase text-[#8B2E1F] font-medium mb-4">
              The Details
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-10">
              Sunday, <br />
              <span className="italic text-[#8B2E1F]">31 May 2026.</span>
            </h2>

            <ul className="space-y-6">
              {[
                {
                  icon: Clock,
                  label: "Time",
                  v: "10:00 AM – 1:00 PM",
                  s: "Doors open at 9:45 AM",
                },
                {
                  icon: MapPin,
                  label: "Venue",
                  v: "ENSEMBLEE",
                  s: "3rd floor, 13th Main Road, 3b4B, 8th Cross Rd, H4, 2nd Stage, Indiranagar, Bengaluru – 560071",
                },
                {
                  icon: IndianRupee,
                  label: "Investment",
                  v: "₹350 total · Includes student & up to 2 parents",
                  s: "Includes tea and Vaastu master class",
                },
                {
                  icon: Users,
                  label: "Who it's for",
                  v: "GenZ and their parents",
                  s: "Limited to 40 attendees per session",
                },
              ].map((d, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex gap-5 border-b border-[#1A1410]/10 pb-6"
                >
                  <div className="shrink-0 h-12 w-12 rounded-full bg-[#FAF3E0] border border-[#8B2E1F]/30 flex items-center justify-center text-[#8B2E1F]">
                    <d.icon size={18} />
                  </div>
                  <div>
                    <div className="text-xs tracking-[0.3em] uppercase text-[#1A1410]/50 mb-1">
                      {d.label}
                    </div>
                    <div className="font-semibold text-lg md:text-xl text-[#1A1410]">{d.v}</div>
                    <div className="text-base text-[#1A1410]/65 mt-1">{d.s}</div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Form column */}
          <div className="md:sticky md:top-28">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative bg-white border border-[#1A1410]/10 rounded-3xl p-7 md:p-10 shadow-[0_20px_80px_-30px_rgba(139,46,31,0.35)]"
            >
              <div className="absolute -top-3 -right-3 bg-[#C9A227] text-[#1A1410] text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1.5 rounded-full">
                Limited seats
              </div>

              <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-2">
                Reserve your seat.
              </h3>
              <p className="text-sm text-[#1A1410]/60 mb-7">
                Pay with Razorpay and we'll email your confirmation + calendar invite.
              </p>

              <form onSubmit={onSubmit} className="space-y-4">
                <Field
                  name="fullName"
                  value={form.fullName}
                  onChange={onChange}
                  placeholder="Full name"
                  required
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="Phone (10-digit)"
                    type="tel"
                    required
                  />
                  <Field
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="Email"
                    type="email"
                    required
                  />
                </div>

                <Field
                  name="school"
                  value={form.school}
                  onChange={onChange}
                  placeholder="School / College (optional)"
                />

                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#1A1410]/55 mb-2 px-1">
                    Parents joining
                  </div>
                  <div className="flex gap-2 p-1 rounded-full bg-[#FAF3E0] border border-[#1A1410]/10">
                    {[0, 1, 2].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, parents: n }))}
                        className={`flex-1 py-2 text-xs sm:text-sm rounded-full transition-all ${form.parents === n
                          ? "bg-[#8B2E1F] text-[#FAF3E0]"
                          : "text-[#1A1410]/60 hover:text-[#1A1410]"
                          }`}
                      >
                        {n === 0 ? "Just me" : n === 1 ? "+ 1 parent" : "+ 2 parents"}
                      </button>
                    ))}
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {form.parents >= 1 && (
                    <motion.div
                      key="parent-fields"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className="space-y-3 overflow-hidden"
                    >
                      <Field
                        name="parent1Name"
                        value={form.parent1Name}
                        onChange={onChange}
                        placeholder="Parent 1 — full name"
                        required
                      />
                      {form.parents >= 2 && (
                        <Field
                          name="parent2Name"
                          value={form.parent2Name}
                          onChange={onChange}
                          placeholder="Parent 2 — full name"
                          required
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Price summary */}
                <div className="mt-1 rounded-2xl bg-[#FAF3E0] border border-[#1A1410]/10 p-4 text-sm">
                  <div className="flex justify-between text-[#1A1410]/70">
                    <span>Student × 1</span>
                    <span>₹{STUDENT_PRICE}</span>
                  </div>
                  {form.parents > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-between text-[#1A1410]/70 mt-1"
                    >
                      <span>
                        Parent × {form.parents}{" "}
                        <span className="text-[#1A1410]/40">(Included)</span>
                      </span>
                      <span>₹0</span>
                    </motion.div>
                  )}
                  <div className="flex justify-between font-semibold text-[#1A1410] mt-3 pt-3 border-t border-[#1A1410]/10">
                    <span>Total · {totalAttendees} {totalAttendees === 1 ? "seat" : "seats"}</span>
                    <span className="font-mono">₹{totalAmount}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="group relative w-full mt-2 bg-[#1A1410] text-[#FAF3E0] py-4 rounded-full font-medium overflow-hidden disabled:opacity-60"
                >
                  <span className="relative z-10 inline-flex items-center justify-center gap-2">
                    {submitting ? "Opening payment…" : `Pay ₹${totalAmount} & confirm`}
                    <Sparkles size={16} className="text-[#C9A227]" />
                  </span>
                  <span className="absolute inset-0 bg-[#8B2E1F] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                </button>

                <div className="flex items-center justify-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#1A1410]/45 mt-1">
                  <span className="h-px w-6 bg-[#1A1410]/20" />
                  Secured by Razorpay
                  <span className="h-px w-6 bg-[#1A1410]/20" />
                </div>

                <AnimatePresence>
                  {status.msg && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className={`flex items-center gap-2 text-sm font-medium px-4 py-3 rounded-xl ${status.ok
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                        }`}
                    >
                      {status.ok && <CheckCircle2 size={16} />}
                      {status.msg}
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>

              <p className="text-[11px] text-[#1A1410]/45 mt-6 text-center leading-relaxed">
                By registering, you agree to receive event-related communication from
                UniThink Education at the contact details provided.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───── CLOSING ───── */}
      <section className="relative py-24 md:py-32 px-4 md:px-8 bg-[#FAF3E0] text-center z-10 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <div className="font-serif text-4xl md:text-7xl leading-[1.05] tracking-tight">
            <div>Good conversations.</div>
            <div className="text-[#1A1410]/40">Better decisions.</div>
            <div className="italic text-[#8B2E1F]">Brighter years.</div>
          </div>
          <button
            onClick={scrollToForm}
            className="mt-12 inline-flex items-center gap-3 bg-[#8B2E1F] text-[#FAF3E0] px-8 py-4 rounded-full font-medium hover:bg-[#1A1410] transition-colors"
          >
            Reserve my seat <ArrowRight size={18} />
          </button>
        </motion.div>
      </section>
    </div >
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Editorial Three-D panel — Domains / Dynamics / Discourse
function ThreeDPanel({ d, idx }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay: idx * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className="group relative overflow-hidden rounded-2xl bg-white border border-[#1A1410]/10 hover:border-[#8B2E1F]/40 transition-all flex flex-col"
    >
      {/* Top — number, category, title, quote */}
      <div className="px-7 md:px-9 pt-9 pb-7 text-center">
        <div className="font-serif text-3xl text-[#1A1410]/35">{d.n}</div>
        <div className="mt-3 text-[10px] tracking-[0.4em] uppercase text-[#1A1410]/55">
          {d.category}
        </div>
        <h3 className="font-serif text-4xl md:text-5xl leading-tight mt-3 text-[#1A1410]">
          {d.title}
        </h3>
        <div className="mx-auto mt-3 h-[2px] w-10 bg-[#8B2E1F]" />
        <p className="font-serif italic text-[#1A1410]/70 mt-5">"{d.quote}"</p>
      </div>

      {/* Image — atmospheric portrait, sepia-toned to match brand */}
      <div
        className={`relative aspect-[4/5] overflow-hidden bg-gradient-to-br ${d.tone}`}
      >
        {d.image && (
          <motion.img
            src={d.image}
            alt={d.title}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter:
                "grayscale(0.55) sepia(0.55) brightness(0.82) contrast(1.08) saturate(0.7)",
            }}
            initial={{ scale: 1.06 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
        {/* Unifying maroon wash so any photo reads as part of the brand */}
        <div className="absolute inset-0 bg-[#8B2E1F]/20 mix-blend-multiply" />
        {/* Warm cream highlight from the top */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF3E0]/15 via-transparent to-transparent" />
        {/* Bottom darken so caption stays readable */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1410]/70 via-[#1A1410]/15 to-transparent" />
        <div className="absolute inset-0 bg-[#8B2E1F]/0 group-hover:bg-[#8B2E1F]/10 transition-colors duration-700" />
        <ThreeDMotif type={d.motif} />
        <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between text-[#FAF3E0]">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FAF3E0]/75">
              Speaker
            </div>
            <div className="font-serif text-lg mt-1 leading-tight">{d.speaker}</div>
          </div>
          <span className="font-mono text-[10px] tracking-[0.3em] text-[#C9A227]">
            {d.n}
          </span>
        </div>
      </div>
    </motion.article>
  );
}

// Discipline motif overlay — small line-art accent per D
function ThreeDMotif({ type }) {
  const common = "absolute top-5 right-5 w-14 h-14 text-[#FAF3E0]/55";
  if (type === "architecture") {
    return (
      <svg className={common} viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M10 22 L30 8 L50 22" />
        <rect x="14" y="22" width="32" height="32" />
        <line x1="14" y1="34" x2="46" y2="34" />
        <line x1="14" y1="44" x2="46" y2="44" />
        <line x1="24" y1="22" x2="24" y2="54" />
        <line x1="36" y1="22" x2="36" y2="54" />
      </svg>
    );
  }
  if (type === "fashion") {
    return (
      <svg className={common} viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M30 6 C 24 8, 22 14, 24 18 C 16 22, 14 36, 18 50 L 12 56 L 48 56 L 42 50 C 46 36, 44 22, 36 18 C 38 14, 36 8, 30 6 Z" />
        <line x1="30" y1="18" x2="30" y2="56" strokeDasharray="2 3" />
      </svg>
    );
  }
  return (
    <svg className={common} viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M8 16 L30 8 L52 16 L30 24 Z" />
      <path d="M18 22 L18 36 C 18 41, 42 41, 42 36 L 42 22" />
      <line x1="48" y1="16" x2="48" y2="32" />
      <circle cx="48" cy="34" r="1.6" fill="currentColor" />
      <path d="M14 46 L30 42 L46 46 L46 54 L30 50 L14 54 Z" />
    </svg>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Speaker card with parallax and hover-reveal bio
function SpeakerCard({ s, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay: idx * 0.12, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="group relative bg-white border border-[#1A1410]/10 rounded-3xl overflow-hidden hover:border-[#8B2E1F]/40 transition-all flex flex-col"
    >
      {/* number badge */}
      <div className="absolute top-5 right-6 font-serif text-5xl text-[#8B2E1F]/15 italic">
        {s.n}
      </div>

      {/* portrait area — image when available, otherwise styled initials */}
      <div className="relative aspect-[4/5] bg-gradient-to-br from-[#1A1410] via-[#3a2620] to-[#8B2E1F] flex items-center justify-center overflow-hidden">
        {s.image ? (
          <>
            <motion.img
              src={s.image}
              alt={s.name}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ scale: 1.08 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1410] via-[#1A1410]/30 to-transparent" />
            <div className="absolute inset-0 bg-[#8B2E1F]/10 mix-blend-multiply opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          </>
        ) : (
          <>
            <motion.div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 30% 30%, #C9A227 0, transparent 50%)",
              }}
              animate={{ opacity: [0.2, 0.4, 0.2] }}
              transition={{ duration: 6, repeat: Infinity }}
            />
            <span className="relative font-serif text-[#FAF3E0]/95 text-[8rem] md:text-[10rem] leading-none font-light">
              {s.initials}
            </span>
          </>
        )}
        <span className="absolute bottom-5 left-6 text-[10px] tracking-[0.35em] uppercase text-[#FAF3E0]/85 z-10">
          {s.field}
        </span>
        <span className="absolute bottom-5 right-6 text-[10px] tracking-[0.2em] uppercase text-[#C9A227] z-10">
          {s.n}
        </span>
      </div>

      <div className="p-6 md:p-7 flex flex-col grow">
        <p className="font-serif italic text-[#8B2E1F] text-lg mb-4">"{s.quote}"</p>
        <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-1">{s.name}</h3>
        <div className="text-xs tracking-[0.15em] uppercase text-[#1A1410]/60 font-medium">
          {s.role}
        </div>
        <div className="text-xs tracking-[0.15em] uppercase text-[#8B2E1F]/80 font-medium mb-4">
          {s.org}
        </div>
        <p className="text-base text-[#1A1410]/80 leading-relaxed mb-5">{s.bio}</p>

        <button
          onClick={() => setOpen((v) => !v)}
          className="mt-auto inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-medium text-[#8B2E1F] hover:gap-3 transition-all"
        >
          {open ? "Hide profile" : "Read profile"}
          <motion.span animate={{ rotate: open ? 180 : 0 }}>
            <ChevronDown size={14} />
          </motion.span>
        </button>

        <AnimatePresence>
          {open && (
            <motion.ul
              initial={{ height: 0, opacity: 0, marginTop: 0 }}
              animate={{ height: "auto", opacity: 1, marginTop: 16 }}
              exit={{ height: 0, opacity: 0, marginTop: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-2 overflow-hidden border-t border-[#1A1410]/10 pt-4"
            >
              {s.bullets.map((b, i) => (
                <li key={i} className="flex gap-2 text-base text-[#1A1410]/80">
                  <span className="text-[#8B2E1F] shrink-0">✦</span>
                  <span>{b}</span>
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </motion.article>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Field({ name, value, onChange, placeholder, type = "text", required }) {
  return (
    <div className="relative group">
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="peer w-full bg-[#FAF3E0]/50 border border-[#1A1410]/15 rounded-xl px-4 py-3.5 text-[#1A1410] placeholder-[#1A1410]/40 focus:outline-none focus:border-[#8B2E1F] focus:bg-white transition-all"
      />
    </div>
  );
}
